import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { ConversationTagWithId } from "@redotech/redo-model/conversation";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { ConversationTagPill } from "./conversation-tag-pill";
import { groupTagsByTagGroups } from "./tag-grouping-utils";

interface GroupedConversationTagsProps {
  currentTags: ConversationTagWithId[];
  onTagRemoved: (tag: ConversationTagWithId) => void;
  disabled?: boolean;
  addButton?: React.ReactNode;
}

export const GroupedConversationTags = memo(function GroupedConversationTags({
  currentTags,
  onTagRemoved,
  disabled = false,
  addButton,
}: GroupedConversationTagsProps) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const tagGroupsResult = useLoad(async () => {
    const response = await rpcClient.getTagGroups({});
    return response.tagGroups;
  }, [rpcClient]);

  const groupedTags = useMemo(() => {
    if (!tagGroupsResult.value || currentTags.length === 0) {
      return { groups: [], ungrouped: currentTags };
    }

    return groupTagsByTagGroups(currentTags, tagGroupsResult.value);
  }, [tagGroupsResult.value, currentTags]);

  if (tagGroupsResult.pending) {
    return <RedoSpinner size="xs" />;
  }

  if (currentTags.length === 0) {
    return addButton ? <Flex>{addButton}</Flex> : null;
  }

  return (
    <Flex dir="column" gap="sm">
      {groupedTags.ungrouped.length > 0 && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            General
          </Text>
          <Flex gap="xs" wrap="wrap">
            {groupedTags.ungrouped.map((tag) => (
              <ConversationTagPill
                badgeSize="xs"
                key={tag.tagId}
                tag={tag}
                xClicked={disabled ? undefined : () => onTagRemoved(tag)}
              />
            ))}
          </Flex>
        </Flex>
      )}

      {groupedTags.groups.map(({ group, tags }) => (
        <Flex dir="column" gap="xs" key={group._id}>
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {group.name}
          </Text>
          <Flex gap="xs" wrap="wrap">
            {tags.map((tag) => (
              <ConversationTagPill
                badgeSize="xs"
                key={tag.tagId}
                tag={tag}
                xClicked={disabled ? undefined : () => onTagRemoved(tag)}
              />
            ))}
          </Flex>
        </Flex>
      ))}

      {addButton && <Flex>{addButton}</Flex>}
    </Flex>
  );
});
